import React from 'react'
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { formatTokens } from '@/lib/currency'

interface TierData {
  tier: string
  total_tokens: number
}

interface TierBreakdownProps {
  data: TierData[]
  height?: number
}

const TIER_COLORS: Record<string, string> = {
  speed: '#22c55e',
  standard: '#3b82f6',
  premium: '#a855f7',
  media: '#f97316',
}

const TIER_LABELS: Record<string, string> = {
  speed: 'Speed',
  standard: 'Standard',
  premium: 'Premium',
  media: 'Media',
}

function CustomTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: Array<{ name: string; value: number; payload: TierData }>
}) {
  if (!active || !payload || payload.length === 0) return null
  const { name, value } = payload[0]

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
      <p className="text-xs font-medium text-gray-500 mb-1 capitalize">
        {TIER_LABELS[name] || name}
      </p>
      <p className="text-sm font-semibold text-gray-800">
        {formatTokens(value)} tokens
      </p>
    </div>
  )
}

function CustomLegend({ payload }: { payload?: Array<{ value: string; color: string }> }) {
  if (!payload) return null
  return (
    <ul className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2">
      {payload.map((entry) => (
        <li key={entry.value} className="flex items-center gap-1.5 text-xs text-gray-600">
          <span
            className="inline-block w-3 h-3 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          {TIER_LABELS[entry.value] || entry.value}
        </li>
      ))}
    </ul>
  )
}

export function TierBreakdown({ data, height = 300 }: TierBreakdownProps) {
  if (!data || data.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-gray-400 text-sm"
        style={{ height }}
      >
        No tier data available
      </div>
    )
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="45%"
          innerRadius={60}
          outerRadius={90}
          paddingAngle={3}
          dataKey="total_tokens"
          nameKey="tier"
        >
          {data.map((entry) => (
            <Cell
              key={entry.tier}
              fill={TIER_COLORS[entry.tier] || '#6366f1'}
            />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
        <Legend content={<CustomLegend />} />
      </PieChart>
    </ResponsiveContainer>
  )
}
